import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Users, Calendar, FileText, MessageSquare, DollarSign, Plus, LogOut, Loader2, X } from "lucide-react";
import { AIOpsPanel } from "../components/admin/AIOpsPanel";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [aiOpsSummary, setAiOpsSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInv, setShowInv] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, c, b, i, m, ai] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/clients"),
        api.get("/admin/bookings"), api.get("/admin/invoices"), api.get("/admin/contacts"), api.get("/admin/ai-ops/summary"),
      ]);
      setStats(s.data); setClients(c.data); setBookings(b.data); setInvoices(i.data); setContacts(m.data); setAiOpsSummary(ai.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="admin-dashboard">
      <header className="border-b border-[var(--border)] glass sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="block">
              <img src="/phm-logo.png" alt="PHM" className="w-9 h-9 object-cover border border-[var(--border)] hover:border-[var(--red)] transition-colors" />
            </Link>
            <div>
              <div className="font-display text-sm font-semibold">Admin Console</div>
              <div className="text-[10px] text-[var(--text-3)] uppercase tracking-[0.2em]">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowInv(true)} className="btn-red !py-2 !px-4 text-sm" data-testid="admin-new-invoice"><Plus className="w-4 h-4" /> New Invoice</button>
            <button onClick={doLogout} className="text-sm text-[var(--text-2)] hover:text-white flex items-center gap-1.5" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight mb-2">Admin Console</h1>
        <p className="text-[var(--text-2)] mb-8">Overview, clients, bookings, invoices, and contact submissions.</p>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          <Stat icon={Users} label="Clients" value={stats?.clients ?? "—"} onClick={() => setTab("clients")} active={tab === "clients"} />
          <Stat icon={Calendar} label="Bookings" value={stats?.bookings ?? "—"} onClick={() => setTab("bookings")} active={tab === "bookings"} />
          <Stat icon={FileText} label="Invoices Billed" value={`$${Number(stats?.invoices_total || 0).toLocaleString()}`} onClick={() => setTab("invoices")} active={tab === "invoices"} />
          <Stat icon={DollarSign} label="Revenue Collected" value={`$${Number(stats?.revenue_collected || 0).toLocaleString()}`} onClick={() => setTab("invoices")} active={tab === "invoices"} />
          <Stat icon={MessageSquare} label="Inquiries" value={stats?.contacts ?? "—"} onClick={() => setTab("contacts")} active={tab === "contacts"} />
        </div>
        <div className="flex gap-1 border-b border-[var(--border)] mb-6 overflow-x-auto">
          {[["overview","Overview"],["clients","Clients"],["bookings","Bookings"],["invoices","Invoices"],["contacts","Contacts"],["aiops","AI Ops"]].map(([k,l])=>(
            <button key={k} onClick={()=>setTab(k)} data-testid={`admin-tab-${k}`}
              className={`px-5 py-3 text-sm font-medium transition relative whitespace-nowrap ${tab===k?"text-white":"text-[var(--text-2)] hover:text-white"}`}>
              {l}{tab===k && <span className="absolute -bottom-px inset-x-0 h-px bg-[var(--red)]" />}
            </button>
          ))}
        </div>

        {loading ? <div className="text-[var(--text-2)] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div> : (
          <>
            {tab === "overview" && (
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="card-dark p-6">
                  <div className="overline mb-4">Recent Bookings</div>
                  {bookings.slice(0,5).map(b => (
                    <div key={b.id} className="py-3 border-b border-[var(--border)] last:border-0 flex justify-between text-sm">
                      <div><div className="font-medium">{b.name}</div><div className="text-xs text-[var(--text-3)]">{b.topic}</div></div>
                      <div className="text-right text-xs text-[var(--text-2)]">{b.preferred_date}<div>{b.status}</div></div>
                    </div>
                  ))}
                  {bookings.length === 0 && <div className="text-sm text-[var(--text-3)]">No bookings yet.</div>}
                </div>
                <div className="card-dark p-6">
                  <div className="overline mb-4">Recent Inquiries</div>
                  {contacts.slice(0,5).map(c => (
                    <div key={c.id} className="py-3 border-b border-[var(--border)] last:border-0 text-sm">
                      <div className="font-medium">{c.name} <span className="text-[var(--text-3)] font-normal">· {c.organization || "—"}</span></div>
                      <div className="text-xs text-[var(--text-2)] mt-1">{c.subject}</div>
                    </div>
                  ))}
                  {contacts.length === 0 && <div className="text-sm text-[var(--text-3)]">No inquiries yet.</div>}
                </div>
              </div>
            )}

            {tab === "clients" && (
              <Table cols={["Name","Email","Organization","Joined"]} data-testid="admin-clients-table"
                rows={clients.map(c=>({key:c.id, cells:[c.name, c.email, c.organization||"—", c.created_at?.slice(0,10)]}))} />
            )}
            {tab === "bookings" && (
              <Table cols={["Name","Type","Date","Time","Topic","Status","Payment"]} data-testid="admin-bookings-table"
                rows={bookings.map(b=>({key:b.id, cells:[b.name, b.type==="paid_in_person"?"Paid In-Person":"Free Virtual", b.preferred_date, b.preferred_time, b.topic, b.status, b.payment_status]}))} />
            )}
            {tab === "invoices" && (
              <Table cols={["Client","Description","Amount","Status","Created"]} data-testid="admin-invoices-table"
                rows={invoices.map(i=>({key:i.id, cells:[`${i.client_name} <${i.client_email}>`, i.description, `$${Number(i.amount).toLocaleString(undefined,{minimumFractionDigits:2})}`, i.status, i.created_at?.slice(0,10)]}))} />
            )}
            {tab === "contacts" && (
              <Table cols={["Name","Email","Org","Subject","Message","When"]} data-testid="admin-contacts-table"
                rows={contacts.map(c=>({key:c.id, cells:[c.name, c.email, c.organization||"—", c.subject, c.message, c.created_at?.slice(0,10)]}))} />
            )}
            {tab === "aiops" && <AIOpsPanel summary={aiOpsSummary} />}
          </>
        )}
      </div>

      {showInv && <NewInvoiceModal onClose={()=>setShowInv(false)} onCreated={()=>{ setShowInv(false); loadAll(); }} />}
    </div>
  );
}

function Stat({ icon: Icon, label, value, onClick, active = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`card-dark p-5 text-left transition w-full hover:border-[var(--red)] hover:bg-[var(--surface-hover)] ${
        active ? "border-[var(--red)]" : ""
      }`}
    >
      <Icon className="w-5 h-5 text-[var(--red)] mb-3" />
      <div className="font-display text-2xl font-semibold">{value}</div>
      <div className="text-xs text-[var(--text-3)] uppercase tracking-[0.2em] mt-1">{label}</div>
    </button>
  );
}

function Table({ cols, rows }) {
  return (
    <div className="card-dark overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--border)]">
            {cols.map(c => <th key={c} className="text-left px-5 py-3 overline">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={cols.length} className="px-5 py-8 text-center text-[var(--text-3)]">No records yet.</td></tr>}
          {rows.map(r => (
            <tr key={r.key} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--surface-hover)]">
              {r.cells.map((cell, i) => <td key={`${r.key}-${i}`} className="px-5 py-3 text-[var(--text-2)] max-w-[260px] truncate">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewInvoiceModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ client_email: "", client_name: "", description: "", amount: "", due_date: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k,v) => setForm(f => ({...f, [k]: v}));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/admin/invoices", { ...form, amount: parseFloat(form.amount), line_items: [] });
      onCreated();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not create invoice.");
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="invoice-modal">
      <form onSubmit={submit} onClick={e=>e.stopPropagation()} className="card-dark p-8 w-full max-w-lg space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline text-[var(--red)] mb-2">New invoice</div>
            <h2 className="font-display text-2xl font-semibold">Create invoice</h2>
          </div>
          <button type="button" onClick={onClose} className="text-[var(--text-2)] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="overline mb-2 block">Client name</label>
            <input required value={form.client_name} onChange={(e)=>set("client_name",e.target.value)} className="input-dark" data-testid="inv-client-name" />
          </div>
          <div>
            <label className="overline mb-2 block">Client email</label>
            <input required type="email" value={form.client_email} onChange={(e)=>set("client_email",e.target.value)} className="input-dark" data-testid="inv-client-email" />
          </div>
        </div>
        <div>
          <label className="overline mb-2 block">Description</label>
          <input required value={form.description} onChange={(e)=>set("description",e.target.value)} className="input-dark" data-testid="inv-description" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="overline mb-2 block">Amount (USD)</label>
            <input required type="number" min="1" step="0.01" value={form.amount} onChange={(e)=>set("amount",e.target.value)} className="input-dark" data-testid="inv-amount" />
          </div>
          <div>
            <label className="overline mb-2 block">Due date</label>
            <input type="date" value={form.due_date} onChange={(e)=>set("due_date",e.target.value)} className="input-dark" data-testid="inv-due" />
          </div>
        </div>
        {error && <div className="text-sm text-[var(--red)]" data-testid="inv-error">{error}</div>}
        <button type="submit" disabled={busy} className="btn-red w-full justify-center" data-testid="inv-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create & email invoice"}
        </button>
      </form>
    </div>
  );
}
